import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import BookingPage from "@/pages/BookingPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerAuth from "@/pages/CustomerAuth";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAppointments from "@/pages/AdminAppointments";
import AdminCustomers from "@/pages/AdminCustomers";
import AdminBarbers from "@/pages/AdminBarbers";
import AdminServices from "@/pages/AdminServices";
import AdminCalendar from "@/pages/AdminCalendar";
import AdminReviews from "@/pages/AdminReviews";
import AdminSettings from "@/pages/AdminSettings";
import AdminPortfolio from "@/pages/AdminPortfolio";
import AdminWorkingHours from "@/pages/AdminWorkingHours";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const GOOGLE_CLIENT_ID = "333563014993-mvrm835v7vt6agjlkkcatnlmjvkilq92.apps.googleusercontent.com";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userType, setUserType] = useState(localStorage.getItem("userType"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const endpoint = userType === "admin" ? "/admin/me" : "/customers/me";
          const response = await axios.get(`${API}${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth init failed:", error);
          logout();
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token, userType]);

  const login = (newToken, userData, type) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("userType", type);
    setToken(newToken);
    setUserType(type);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userType");
    setToken(null);
    setUserType(null);
    setUser(null);
  };

  // Axios interceptor: auto-logout on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, userType, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredType }) => {
  const { token, userType, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to={requiredType === "admin" ? "/admin/login" : "/auth"} replace />;
  }

  if (requiredType && userType !== requiredType) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Ignore if already seeded
      }
    };
    seedData();
  }, []);

  return (
    <AuthProvider>
      <div className="min-h-screen bg-[#fafafa]">
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#ffffff',
              color: '#1a1a2e',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            },
          }}
        />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/auth" element={<CustomerAuth />} />

            {/* Customer Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute requiredType="customer">
                <CustomerDashboard />
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedRoute requiredType="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/appointments" element={
              <ProtectedRoute requiredType="admin">
                <AdminAppointments />
              </ProtectedRoute>
            } />
            <Route path="/admin/customers" element={
              <ProtectedRoute requiredType="admin">
                <AdminCustomers />
              </ProtectedRoute>
            } />
            <Route path="/admin/barbers" element={
              <ProtectedRoute requiredType="admin">
                <AdminBarbers />
              </ProtectedRoute>
            } />
            <Route path="/admin/services" element={
              <ProtectedRoute requiredType="admin">
                <AdminServices />
              </ProtectedRoute>
            } />
            <Route path="/admin/calendar" element={
              <ProtectedRoute requiredType="admin">
                <AdminCalendar />
              </ProtectedRoute>
            } />
            <Route path="/admin/reviews" element={
              <ProtectedRoute requiredType="admin">
                <AdminReviews />
              </ProtectedRoute>
            } />
            <Route path="/admin/portfolio" element={
              <ProtectedRoute requiredType="admin">
                <AdminPortfolio />
              </ProtectedRoute>
            } />
            <Route path="/admin/working-hours" element={
              <ProtectedRoute requiredType="admin">
                <AdminWorkingHours />
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredType="admin">
                <AdminSettings />
              </ProtectedRoute>
            } />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
